/* Settings — STORY-006.
 *
 * Two of the seven Owner-only actions live here: renaming the workspace, and
 * changing who may do what. The other five arrive with their own stories.
 *
 * **The Member's view is the part worth getting right.** An Owner-only control
 * is explained in TEXT, never rendered as a dead control with no reason —
 * `design.md` rejects that pattern outright, and a Member who cannot tell
 * whether the product is broken or they are unprivileged will assume broken.
 *
 * Hiding a control is presentation and never a control: every one of these
 * actions is refused server-side by the auth policy regardless of what this
 * screen renders. `security.md` A01.
 */

import {
  type Invitation,
  type Member,
  PERSON_RETENTION_MONTHS,
  type PendingJoinRequest,
  type PersonRetentionMonths,
} from '@eventuley/contracts';
import { formatDate } from '@eventuley/i18n';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextField, UpgradePrompt } from '../../../shared/ui/index.js';
import { useCurrentUser } from '../../auth/index.js';
import {
  useApproveJoinRequest,
  useChangeMemberRole,
  useDeclineJoinRequest,
  useEnabledValues,
  useInvitations,
  useInviteMember,
  useLeaveOrganisation,
  useMembers,
  useOrganisation,
  usePendingJoinRequests,
  useRemoveMember,
  useRenameOrganisation,
  useRevokeInvitation,
  useSetPersonRetention,
} from '../api/index.js';
import { EnabledSet } from '../components/enabled-set.js';

export function SettingsScreen() {
  const t = useTranslate();
  const { organisationId = '' } = useParams({ strict: false });
  const navigate = useNavigate();

  const { data: user } = useCurrentUser();
  const { data: organisation } = useOrganisation(organisationId);
  const { data: roster } = useMembers(organisationId);
  const rename = useRenameOrganisation(organisationId);
  const setPersonRetention = useSetPersonRetention(organisationId);
  const inviteMember = useInviteMember(organisationId);
  const revoke = useRevokeInvitation(organisationId);
  const removeMember = useRemoveMember(organisationId);
  const changeRole = useChangeMemberRole(organisationId);
  const leave = useLeaveOrganisation(organisationId);
  const {
    data: enabledValues,
    error: enabledValuesError,
    isPending: enabledValuesLoading,
  } = useEnabledValues(organisationId);

  const [name, setName] = useState('');
  /* Local until saved, so choosing a period and changing your mind before
     pressing the button erases nothing — STORY-043. */
  const [retention, setRetention] = useState<PersonRetentionMonths | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  /* Which removal is being confirmed. Null is "none in progress" — held here
   * rather than per row so two confirmations cannot be open at once. */
  const [confirming, setConfirming] = useState<Member | null>(null);
  /* Whether the leave confirmation is open — STORY-009. Separate from the
   * removal above: they are different subjects and different refusals. */
  const [leaving, setLeaving] = useState(false);
  /* Whether leaving would empty the workspace. Read from the roster the screen
   * already holds, because it decides which of the two refusals is true and the
   * server deliberately answers one line for both. */
  const alone = (roster?.members ?? []).length === 1;
  /* What the last successful change was, for the announcement below. Held here
   * rather than derived from the roster, because the roster after a change no
   * longer says which row moved. */
  const [lastChanged, setLastChanged] = useState<{ name: string; role: string } | null>(null);
  /* Seeded from the server once it arrives rather than held as a second copy:
   * the field is the draft, the query is the truth. */
  useEffect(() => {
    if (organisation?.name) setName(organisation.name);
  }, [organisation?.name]);

  /* Same shape, and `null` is a legitimate stored value here rather than an
   * absence — so the guard is on the query having arrived, not on the value
   * being truthy. Seeding on `if (value)` would leave the control showing Off
   * for a workspace that had chosen Off, which is right by accident, and would
   * silently reset a chosen period to Off on a refetch, which is not. */
  useEffect(() => {
    if (organisation)
      setRetention((organisation.personRetentionMonths ?? null) as PersonRetentionMonths | null);
  }, [organisation]);

  const membership = user?.memberships.find((held) => held.organisationId === organisationId);
  const isOwner = membership?.role === 'owner';

  /* Gated on the Role. Owner-only on the server, which answers 403 to a
   * Member — asking anyway would put a red error in every Member's console on
   * every load, and hiding the result is presentation, never a control. */
  const { data: requests } = usePendingJoinRequests(organisationId, isOwner);
  const approve = useApproveJoinRequest(organisationId);
  const decline = useDeclineJoinRequest(organisationId);
  const { formatLocale } = useI18n();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const approvalRefusal = seatLimitFrom(approve.error);
  const { data: pending } = useInvitations(organisationId, isOwner);

  /* Read from `fields`, never by parsing the message — `coding.md` branches on
   * the code and the client builds its own copy from the parts. */
  const seatLimit = seatLimitFrom(inviteMember.error);

  /* Nothing is rendered until the Role is known. Defaulting to the Member view
   * while the User loads flashes the wrong permission at an Owner — briefly
   * telling them they may not rename their own workspace — and defaulting the
   * other way would flash controls that do not belong to a Member. */
  if (!user) {
    return (
      <div className="page-head">
        <h1>{t('settings.title')}</h1>
        <p className="field__hint">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>{t('settings.title')}</h1>
      </div>

      <section className="settings__section">
        <h2>{t('settings.general')}</h2>

        {isOwner ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate(name);
            }}
            noValidate
          >
            <TextField
              label={t('settings.name_label')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Button type="submit" busy={rename.isPending}>
              {rename.isPending ? t('common.submitting') : t('settings.rename')}
            </Button>
            {rename.isSuccess ? <Callout tone="success">{t('settings.renamed')}</Callout> : null}
          </form>
        ) : (
          <>
            <TextField label={t('settings.name_label')} value={organisation?.name ?? ''} readOnly />
            {/* The reason, in words. Not a greyed control and not a tooltip. */}
            <p className="field__hint">{t('settings.owner_only')}</p>
          </>
        )}
      </section>

      {/* How long inactive People are kept — STORY-043.
          Its own section rather than a field in "general", because it is the
          only setting on this screen that destroys data. */}
      <section className="card">
        <h2>{t('settings.retention.heading')}</h2>
        <p className="field__hint">{t('settings.retention.explanation')}</p>

        {isOwner ? (
          <>
            <label className="field">
              <span className="field__label">{t('settings.retention.label')}</span>
              {/* A real `<select>` over a fixed ladder, not a number field.
                  `design.md` prefers the OS control — keyboard and screen
                  reader correct for free — and the ladder exists because the
                  direction of the mistake is destructive: a typo of 1 would
                  erase the whole contact book on the next sweep. */}
              <select
                className="field__control"
                value={retention === null ? 'off' : String(retention)}
                onChange={(event) => {
                  const chosen = event.target.value;
                  setRetention(chosen === 'off' ? null : (Number(chosen) as PersonRetentionMonths));
                }}
              >
                <option value="off">{t('settings.retention.off')}</option>
                {PERSON_RETENTION_MONTHS.map((months) => (
                  <option key={months} value={String(months)}>
                    {t('settings.retention.months', { count: months })}
                  </option>
                ))}
              </select>
            </label>

            {/* Shown BEFORE they save, not after. Announced rather than only
                shown — `design.md` requires the warning to reach a screen
                reader, and this is the one control on the page whose mistake
                cannot be undone. */}
            {retention !== null ? (
              <Callout tone="warning">{t('settings.retention.warning')}</Callout>
            ) : null}

            <Button
              type="button"
              busy={setPersonRetention.isPending}
              onClick={() => setPersonRetention.mutate(retention)}
            >
              {setPersonRetention.isPending ? t('common.submitting') : t('settings.retention.save')}
            </Button>

            {setPersonRetention.isSuccess ? (
              <Callout tone="success">{t('settings.retention.saved')}</Callout>
            ) : null}
          </>
        ) : (
          <>
            <p className="field__hint">
              {organisation?.personRetentionMonths == null
                ? t('settings.retention.off')
                : t('settings.retention.months', { count: organisation.personRetentionMonths })}
            </p>
            <p className="field__hint">{t('settings.retention.owner_only')}</p>
          </>
        )}
      </section>

      <section className="settings__section">
        <h2>{t('settings.members')}</h2>
        <p className="field__hint">{t('settings.members_body')}</p>

        {changeRole.error ? <RoleError error={changeRole.error} /> : null}

        {/* A live region, so a screen-reader user hears that the change landed.
            Without it the only feedback is a select that already looked changed
            the moment it was operated. */}
        <p className="sr-only" role="status">
          {changeRole.isSuccess && lastChanged
            ? t('settings.role_changed', { name: lastChanged.name, role: lastChanged.role })
            : ''}
        </p>

        <ul className="row-list settings__roster">
          {(roster?.members ?? []).map((member) => (
            /* The design library calls these `row`, `row__main`, `row__title`
               and `row__meta`. They were written as `list-row__*` — after the
               directory rather than the component — so the roster rendered
               with no card, no layout and no muted address, and nothing
               failed. `design.md`: a component’s markup here and its markup
               in the mockup are the same markup. */
            <li key={member.userId} className="row">
              <span className="row__main">
                <span className="row__title">{member.displayName}</span>
                <span className="row__meta">{member.email}</span>
              </span>

              {isOwner ? (
                <RoleSelect
                  member={member}
                  busy={changeRole.isPending}
                  onChange={(role) => {
                    setLastChanged({
                      name: member.displayName,
                      role: role === 'owner' ? t('role.owner') : t('role.member'),
                    });
                    changeRole.mutate({ userId: member.userId, role });
                  }}
                />
              ) : (
                /* Read-only, and still readable: the Role is text rather than a
                 * disabled control, so nothing about it invites a click. */
                <span className="badge">
                  {member.role === 'owner' ? t('role.owner') : t('role.member')}
                </span>
              )}

              {isOwner ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    /* Clear the last refusal before opening a different
                       confirmation. A mutation error outlives the subject it
                       was about, so "always keeps at least one Owner" — said
                       of the last Owner — stayed on screen when the next
                       person opened was an ordinary Member whose removal
                       would have succeeded. */
                    removeMember.reset();
                    setConfirming(member);
                  }}
                >
                  <span className="sr-only">
                    {t('invite.remove', { name: member.displayName })}
                  </span>
                  <span aria-hidden="true">−</span>
                </Button>
              ) : null}
            </li>
          ))}
        </ul>

        {confirming ? (
          <Callout tone="warning">
            <strong>{t('invite.remove_confirm', { name: confirming.displayName })}</strong>
            {/* What SURVIVES, not just what goes. The fear when removing
                somebody is losing their work, and none of it moves. */}
            <p>{t('invite.remove_explains')}</p>
            {removeMember.error instanceof ApiError && removeMember.error.code === 'conflict' ? (
              <p>{t('invite.last_owner')}</p>
            ) : null}
            <div className="btn-row">
              <Button
                busy={removeMember.isPending}
                onClick={() =>
                  removeMember.mutate(confirming.userId, { onSuccess: () => setConfirming(null) })
                }
              >
                {t('invite.remove_yes')}
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                {t('invite.remove_cancel')}
              </Button>
            </div>
          </Callout>
        ) : null}

        {isOwner ? null : <p className="field__hint">{t('settings.owner_explains')}</p>}
      </section>

      {isOwner ? (
        <section className="settings__section">
          <h2>{t('invite.heading')}</h2>

          {seatLimit ? (
            <UpgradePrompt
              lead={t('invite.seat_limit_lead', { plan: planLabel(seatLimit.plan) })}
              limit={t('invite.seat_limit_limit')}
              alternative={t('invite.seat_limit_alternative')}
              action={t('invite.see_plans')}
              label={t('invite.seat_limit_lead', { plan: planLabel(seatLimit.plan) })}
              onAction={() => {
                void navigate({ to: '/plans', search: { workspace: organisationId } });
              }}
            />
          ) : null}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              inviteMember.mutate(
                { email: inviteEmail, role: inviteRole as 'owner' | 'member' },
                { onSuccess: () => setInviteEmail('') },
              );
            }}
            noValidate
          >
            <TextField
              label={t('invite.email_label')}
              type="email"
              autoComplete="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
            />
            <label className="field">
              <span className="field__label">{t('invite.role_label')}</span>
              <select
                className="field__control"
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value)}
              >
                <option value="member">{t('role.member')}</option>
                <option value="owner">{t('role.owner')}</option>
              </select>
            </label>
            <Button type="submit" busy={inviteMember.isPending}>
              {inviteMember.isPending ? t('common.submitting') : t('invite.submit')}
            </Button>
          </form>

          <h3>{t('invite.pending')}</h3>
          {(pending?.invitations ?? []).length === 0 ? (
            <p className="field__hint">{t('invite.none')}</p>
          ) : (
            <ul className="row-list settings__roster">
              {(pending?.invitations ?? []).map((invitation: Invitation) => (
                <li key={invitation.id} className="row">
                  <span className="row__main">
                    <span className="row__title">{invitation.email}</span>
                    {/* The code, visible. It is useless without the address
                        beside it, and being read aloud is its entire job. */}
                    <span className="row__meta">{invitation.code}</span>
                  </span>
                  <Button
                    variant="ghost"
                    /* Scoped to THIS invitation. One mutation object serves
                       every row, so an unscoped pending flag spun every
                       spinner in the list and disabled buttons nobody
                       pressed — the screen said two invitations were being
                       withdrawn when one was. */
                    busy={revoke.isPending && revoke.variables === invitation.id}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    {/* Named after the invitation it withdraws: a column of
                        identical "Withdraw" buttons announces the same thing on
                        every row. */}
                    <span className="sr-only">
                      {t('invite.revoke', { email: invitation.email })}
                    </span>
                    <span aria-hidden="true">×</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {/* Asking to join — STORY-008. Owner-only, and absent rather than empty
          for a Member: there is nothing here they may act on, and a card
          headed "Asking to join" with nothing under it invites the question of
          what they are missing. */}
      {isOwner && (requests?.requests ?? []).length > 0 ? (
        <section className="card settings__section">
          <h2>{t('requests.heading')}</h2>
          {/* Said where the decision is made, not only in a document: this is
              why an Organisation ID may travel in a URL at all. */}
          <p className="field__hint">{t('requests.body')}</p>

          {approvalRefusal ? (
            <UpgradePrompt
              lead={t('requests.seat_limit', { plan: planLabel(approvalRefusal.plan) })}
              limit={t('requests.seat_limit_limit')}
              alternative={t('invite.seat_limit_alternative')}
              action={t('invite.see_plans')}
              label={t('requests.seat_limit', { plan: planLabel(approvalRefusal.plan) })}
              onAction={() => {
                void navigate({ to: '/plans', search: { workspace: organisationId } });
              }}
            />
          ) : null}

          <ul className="row-list settings__roster">
            {(requests?.requests ?? []).map((joinRequest: PendingJoinRequest) => (
              <li key={joinRequest.id} className="row">
                <span className="row__main">
                  <span className="row__title">{joinRequest.displayName}</span>
                  <span className="row__meta">
                    {joinRequest.email} ·{' '}
                    {t('requests.asked', {
                      when: formatDate(new Date(joinRequest.requestedAt), {
                        locale: formatLocale,
                        timeZone,
                      }),
                    })}
                  </span>
                </span>
                <Button
                  variant="secondary"
                  busy={approve.isPending && approve.variables === joinRequest.id}
                  onClick={() => approve.mutate(joinRequest.id)}
                >
                  {/* Two names per row: the visible label is short enough to
                      sit in a column, and the accessible one carries the
                      subject — a column of "Approve" announces nothing. */}
                  <span className="sr-only">
                    {t('requests.approve', { name: joinRequest.displayName })}
                  </span>
                  <span aria-hidden="true">{t('requests.approve_label')}</span>
                </Button>
                <Button
                  variant="ghost"
                  busy={decline.isPending && decline.variables === joinRequest.id}
                  onClick={() => decline.mutate(joinRequest.id)}
                >
                  <span className="sr-only">
                    {t('requests.decline', { name: joinRequest.displayName })}
                  </span>
                  <span aria-hidden="true">{t('requests.decline_label')}</span>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The enabled set — STORY-041. Two instances of one component, not two
          designs: currencies and timezones behave identically, and the only
          things that differ are the copy and the options.

          Visible to a Member too, read-only. What a Member may pick from is
          exactly what this list holds, so hiding it would leave them unable to
          find out why a currency they need is missing — and the note says who
          to ask. */}
      {organisation ? (
        <>
          <EnabledSet
            kind="currency"
            organisationId={organisationId}
            own={organisation.currency}
            enabled={(enabledValues?.values ?? []).filter((row) => row.kind === 'currency')}
            loadError={enabledValuesError}
            loading={enabledValuesLoading}
            canManage={isOwner}
          />
          <EnabledSet
            kind="timezone"
            organisationId={organisationId}
            own={organisation.timezone}
            enabled={(enabledValues?.values ?? []).filter((row) => row.kind === 'timezone')}
            loadError={enabledValuesError}
            loading={enabledValuesLoading}
            canManage={isOwner}
          />
        </>
      ) : null}

      {/* Leaving — STORY-009. Every Role sees it, Owners included: what stops
          an Owner leaving is the last-Owner guard in the write, not their Role,
          and hiding the control from them would misreport that as a permission
          they lack rather than a state they can fix. */}
      <section className="settings__section">
        <h2>{t('settings.leave.heading')}</h2>
        {/* What SURVIVES, first. The fear on this button is losing your own
            work, and none of it moves. */}
        <p className="field__hint">{t('settings.leave.body')}</p>

        {leaving ? (
          <Callout tone="warning">
            <strong>{t('settings.leave.confirm', { name: organisation?.name ?? '' })}</strong>
            <p>{t('settings.leave.explains')}</p>
            {leave.error ? <LeaveError error={leave.error} alone={alone} /> : null}
            <div className="btn-row">
              <Button
                busy={leave.isPending}
                onClick={() =>
                  leave.mutate(undefined, {
                    /* Replaced, not pushed: Back from the chooser would return
                       to a workspace they are no longer in, which now answers
                       404 and bounces them straight out again. */
                    onSuccess: () => void navigate({ to: '/next', replace: true }),
                  })
                }
              >
                {t('settings.leave.yes')}
              </Button>
              <Button variant="ghost" onClick={() => setLeaving(false)}>
                {t('settings.leave.cancel')}
              </Button>
            </div>
          </Callout>
        ) : (
          <Button
            variant="ghost"
            onClick={() => {
              /* Clear the last refusal before reopening. A mutation error
                 outlives what it was about, so "you are the only Owner" would
                 still be on screen after somebody else had been promoted. */
              leave.reset();
              setLeaving(true);
            }}
          >
            {t('settings.leave.submit')}
          </Button>
        )}
      </section>
    </>
  );
}

/** The refusal's parts, or null when the failure was something else. */
function seatLimitFrom(error: unknown): { readonly plan: string } | null {
  if (!(error instanceof ApiError) || error.code !== 'entitlement_exceeded') return null;
  if (!error.fields.some((field) => field.path === 'seats')) return null;

  const plan = error.fields.find((field) => field.path === 'plan')?.rule;
  return plan ? { plan } : null;
}

/* The plan's own name, capitalised for a sentence. Not a catalogue key: the
 * server names a plan CODE and a future tier would have no key here, which is
 * the mislabelling the plans table was already corrected for. */
const planLabel = (code: string) => code.charAt(0).toLocaleUpperCase('en-US') + code.slice(1);

function RoleSelect({
  member,
  busy,
  onChange,
}: {
  readonly member: Member;
  readonly busy: boolean;
  readonly onChange: (role: string) => void;
}) {
  const t = useTranslate();

  return (
    <label className="settings__role">
      {/* Named after the person it changes. Eight identical "Role" labels down a
          column tell a screen-reader user nothing about which row they are on. */}
      <span className="sr-only">{t('settings.role_label', { name: member.displayName })}</span>
      {/* A real `<select>`: two options, so a combo box would be unwarranted and
          the OS draws a popup that is keyboard- and screen-reader-correct for
          free — `design.md`. */}
      <select
        className="field__control"
        value={member.role}
        disabled={busy}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="owner">{t('role.owner')}</option>
        <option value="member">{t('role.member')}</option>
      </select>
    </label>
  );
}

/**
 * Why leaving was refused, in the terms of the person reading it.
 *
 * The conflict has two shapes and the server answers one line for both, because
 * it is the client that holds the roster. An Owner with colleagues promotes
 * somebody. An Owner who is the workspace's ONLY Member cannot — on Free there
 * is one seat, so they cannot even invite the person they would promote — and
 * their way out is closing the workspace, which is STORY-010 and not built.
 * Saying "make somebody else an Owner" to them is an instruction that cannot be
 * followed, and that is the failure this branch exists to avoid.
 */
function LeaveError({ error, alone }: { readonly error: unknown; readonly alone: boolean }) {
  const t = useTranslate();

  /* Branching on the CODE, never the message — `coding.md`. */
  if (error instanceof ApiError && error.code === 'conflict') {
    return <p>{alone ? t('settings.leave.only_member') : t('settings.leave.last_owner')}</p>;
  }
  if (error instanceof NetworkError) return <p>{t('error.network')}</p>;
  /* Anything else still says something. Silence here leaves the confirmation
   * open and the button idle, and the reader cannot tell whether they left. */
  return <p>{t('error.unexpected')}</p>;
}

function RoleError({ error }: { readonly error: unknown }) {
  const t = useTranslate();

  /* Branching on the CODE, never the message — `coding.md`. */
  if (error instanceof ApiError && error.code === 'conflict') {
    return <Callout tone="warning">{t('settings.last_owner')}</Callout>;
  }
  if (error instanceof ApiError && error.code === 'forbidden') {
    return <Callout tone="danger">{t('settings.owner_only')}</Callout>;
  }
  return <Callout tone="danger">{t('error.unexpected')}</Callout>;
}
